// tslint:disable max-func-body-length
// tslint:disable:max-line-length
// #region imports
import { createFormatters, createUTCDate, dateToUTC, parseAsDateUTC } from 'app/infrastructure/data/date';
import { invariant } from 'app/infrastructure/log';
import { 
  ComponentDataShape, ConnectedComponentProps, 
  ObjectMap, ComponentMetadata
} from 'app/infrastructure/ui/types-uischema';
import { withConditionalRender } from 'app/infrastructure/ui/renderer/conditional-render';
import { withFallback } from 'app/infrastructure/ui/renderer/fallback';
import { getMemoized, memoize } from 'app/infrastructure/ui/renderer/property-cache';
import { ApplicationState } from 'app/types-store';
import { connect } from 'react-redux';
import { buildEvents } from '../builder/event-builder';
import { ComponentAsyncData, UiState } from '../form/types';
import { InitialUiEngineProps } from '../types-uischema';
import { createIterator } from './iterator';
import {
  getComponentData,
  getComponentMetadata,
  getComponentValues,
  getEntityContext,
  getInspectorPropsWhenDebugging
} from './resolvers';
import { getIteratorContext } from './iterator-resolvers';
import get from 'lodash/get';

// #endregion

/**
 * Convert async loaded component data from store format to the component interface data shape
 * 
 * @param componentData {ComponentAsyncData} store data
 */
const componentAsyncData = (componentData: { options: ComponentAsyncData, categoryOptions: ComponentAsyncData }): ComponentDataShape => 
{
  return componentData && {
    options: componentData.options.data,
    loadOptions: componentData.options.args,
    loadOptionsError: componentData.options.error || undefined,
    fetching: componentData.options.fetching,
    categoryOptions: componentData.categoryOptions.data,
    loadCategoryOptions: componentData.categoryOptions.args,
    loadCategoryOptionsError: componentData.categoryOptions.error || undefined,
    fetchingCategories:  componentData.categoryOptions.fetching
  };
};

/**
 * Components might register `selectors` where they can select extra props
 * from the redux store to apply to a specialized component. If so, we
 * run the selectors here and return the props to merge as component props.
 */
const propsFromRegisteredSelectors = (state: UiState, props: ConnectedComponentProps, fullState: ApplicationState) => {
  let extraWrapperProps = {};
  if (props.componentDescriptor.selectors) {
    extraWrapperProps = props.componentDescriptor
      .selectors.reduce(
        (aggrProps, wrapper) => ({ 
          ...aggrProps, 
          ...wrapper(state, { ...props, ...aggrProps }, fullState) 
        }), {}
      );
  }
  return extraWrapperProps;
};

/**
 * ReadOnly settings from db config has precedence over metadata in store, meaning that
 * setting readonly in config overrides every later value.
 * Store metadata contains read-only information both from case data fields and domains list.
 */
const readOnlyProps = (metaDataPropsFromStore: ComponentMetadata, properties: ObjectMap) => ({
  readOnly: properties.readOnly || metaDataPropsFromStore.readOnly
});

/**
 * Domains are required always or may become so after data changes. 
 * This information is part of case metadata for each domain, and may change between temp saves.
 */
const requiredProps = (metaDataPropsFromStore: ComponentMetadata, properties: ObjectMap) => ({
  label: metaDataPropsFromStore.requiredField ? {
    ...(properties && properties.label),
    ...{
      required: metaDataPropsFromStore.requiredField
    }
  } : properties && properties.label
});

const visibleProps = (metaDataPropsFromStore: ComponentMetadata, properties: ObjectMap) => ({
  visible: properties.visible && !metaDataPropsFromStore.hidden
});

/**
 * Validation errors from store
 */
const validationErrorsAsValidationProp = ({ validation, validationDescendants }: Partial<ComponentMetadata>) => ({ validation, validationDescendants });

/**
 * Extract relevant props from redux store based on current State and host component settings
 */
const propsSpecificToThisComponent = (state: UiState, props: InitialUiEngineProps ) => ({
  resolveContext: getEntityContext(state, props),
  valueProps: getComponentValues(state, props),
  metadataProps: getComponentMetadata(state, props),
  componentData: getComponentData(state, props.componentDescriptor, getEntityContext(state, props)),
  inspectorProps: getInspectorPropsWhenDebugging(state, props)
});

/**
 * Locale dependent date formatting and parsing functions that components must use to creat eand format dates, and parse date input
 * 
 * @param locale 
 */
const dateFunctions = (locale: string) => ({
  ...createFormatters(locale),
  ...{
    createDate: createUTCDate,
    cloneDate: dateToUTC,
    parseDate: parseAsDateUTC
  }
});

/**
 * This is the final **mapStateToProps** function where the projection of configuration and data defines 
 * what and how a component renders
 * 
 * @param hostProps react props of the component hosting this individual component
 */
export function createStateToPropsSelector(
  configProps: InitialUiEngineProps) {

  return (appState: ApplicationState): ConnectedComponentProps => {

    const areaState = get(appState, configProps.statePath);

    invariant(!!areaState, `Containers state path "${configProps.statePath}" does not exist. Check reducers and default state shape.`);

    const {
      resolveContext,
      valueProps,
      metadataProps,
      componentData,
      inspectorProps
    } = propsSpecificToThisComponent(areaState, configProps);

    const hostProps = configProps.hostProps();
    const { locale: currentLocale = hostProps.intl.locale } = appState.app;
    const { componentDescriptor, detailsViewDescriptor, actions } = configProps;
    const { sequence, componentName, properties, id, componentDefaultProps } = componentDescriptor;

    // All props that could be changed dynamically when user does something,
    //  (on every componentWillReceiveProps) 
    //  should be passed as props to cache for [deep] equality check on next cache lookup
    //  This way we only re-render individual components, instead of a complete tree branch
    let currentUiProps = {
      resolveContext,
      ...valueProps,
      ...{ hostProps: configProps.hostProps },
      ...{ componentDescriptor, detailsViewDescriptor },
      ...readOnlyProps(metadataProps, properties),
      ...requiredProps(metadataProps, properties),
      ...visibleProps(metadataProps, properties),
      ...validationErrorsAsValidationProp(metadataProps),
      ...componentAsyncData(componentData)
    };

    // use merged props as argument to selectors, not props
    currentUiProps = { ...currentUiProps, ...propsFromRegisteredSelectors(areaState, currentUiProps, appState) };

    // iterator props are resolved using both configured props and the dynamically generated props.
    // This allows the iterator to display children based on selections from the case data.
    const iteratorProps = getIteratorContext(areaState, configProps, currentUiProps);
    currentUiProps = { ...currentUiProps, ...iteratorProps };

    // Avoid wasted re-renders, immutable comparisions would invalidate props 
    const cachedProps = getMemoized(resolveContext, id, currentUiProps);
    if (cachedProps) {
      return { ...cachedProps, ...inspectorProps };
    }

    // Resulting props for the components first render. Future renders will receive props trough cache merge.
    const finalStateAsProps: ConnectedComponentProps = {
      ...componentDefaultProps,

      // Components may use this for tabIndex for the relevant input control
      sequence,

      // Useful when inspecting
      componentName,

      // CSS identifier for the rendered component type
      className: (componentName || 'not-a-control-type').toLowerCase(),

      // Active locale
      locale: currentLocale,

      // Consistently apply id on root element of component. Stringify to avoid react warning.
      componentId: Number(id).toString(),

      // allow runtime inspection
      ...inspectorProps,

      // add properties built from "domain attributes"
      ...properties,

      // event handlers
      ...buildEvents(actions, componentDescriptor, resolveContext),

      // opt-in selectors for components
      ...currentUiProps,

      // Components may use formatters to format date, time and numbers
      ...dateFunctions(currentLocale)
    };

    memoize(resolveContext, id, finalStateAsProps);

    return finalStateAsProps;

  };
}

export function createComponent(props: InitialUiEngineProps) {
  const { componentDescriptor } = props;
  let component = componentDescriptor.componentClass || null;

  if (componentDescriptor.enhancers) {
    component = componentDescriptor
      .enhancers
      .reduce((aggrComponent, wrapper) => wrapper(aggrComponent, props), component);
  }

  if (componentDescriptor.iterate) {
    component = createIterator(component);
  }
  return component;
}

export function applyMandatoryEnhancers(component: React.ComponentClass<ConnectedComponentProps>) {
  let enhancedComponent: any = withConditionalRender(component, (props: any) => props.visible !== false);
  enhancedComponent = withFallback(enhancedComponent);
  return enhancedComponent;
}

/**
 * Provides a individual Field in a View with data using provide/connect to map dynamic props to the component
 * Any property derived from state or data is built and resolved here
 */
export function connectComponent(rootProps: InitialUiEngineProps) {
  let component = createComponent(rootProps);
  component = applyMandatoryEnhancers(component);
  const mapStateToProps = createStateToPropsSelector(rootProps);
  return connect(mapStateToProps)(component);
}

/**
 * Hides redux-connect dependency from callers
 */
export function connectIteratorComponent(mapStateToProps: (state: ApplicationState) => ConnectedComponentProps) {
  return (Component: React.ComponentClass<any>) => {
    return connect(mapStateToProps)(Component);
  };
}
